<script setup lang="ts">
import { type SidebarTranslationKey } from '@/assets/i18n'
import { AvatarInfo } from '@/components'
import { primeVueTwMerger } from '@/components/primevue'
import { useFeatureFlags } from '@/composable/services/useFeatureFlags'
import { useHealthCheck } from '@/composable/services/useHealthCheck'
import { useLayout } from '@/composable/useLayout'
import { useLogout } from '@/composable/useLogout'
import SidebarLayout from '@/layouts/SidebarLayout.vue'
import type { PermissionName } from '@/model/permissions'
import { useLocalization } from '@/plugins/localization'
import { routeNames, routePaths, type RouteName } from '@/router/router-constants'
import { routes } from '@/router/routes'
import { useUserStore } from '@/stores/user.store'
import { storeToRefs } from 'pinia'
import type { MenuItem } from 'primevue/menuitem'
import { useConfirm } from 'primevue/useconfirm'
import { computed, onMounted, ref } from 'vue'
import {
  RouterLink,
  useRoute,
  useRouter,
  type RouteLocation,
  type RouteRecordRaw,
} from 'vue-router'

const appVersion = import.meta.env.VITE_APP_VERSION
const { onSidebarLinkClicked } = useLayout()
const logout = useLogout()
const router = useRouter()
const currentRoute = useRoute()
const confirm = useConfirm()
const { t } = useLocalization()
const userStore = useUserStore()
const { user } = storeToRefs(userStore)
const { hasPermission } = userStore
const { isFeatureActive } = useFeatureFlags()
const { data: serverVersion } = useHealthCheck().serverVersion()

const handleLogout = () => {
  confirm.require({
    message: t('logout.dialogMessage'),
    header: t('logout.header'),
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: t('cancel'),
    acceptLabel: t('logout.confirm'),
    accept: logout,
  })
}

const routePermissions: Record<string, PermissionName> = {}
const savePermissionsOf = (route: RouteRecordRaw) => {
  const routeName = route.name?.toString() || 'default'
  if (route.meta?.permission) {
    routePermissions[routeName] = route.meta.permission
  }
  for (const child of route.children || []) {
    savePermissionsOf(child)
  }
}
routes.map(savePermissionsOf)

const sidebarItems: { name?: SidebarTranslationKey; routeNames: RouteName[] }[] = [
  { routeNames: [routeNames.home] },
  { routeNames: [routeNames.cases] },
  { routeNames: [routeNames.practices] },
  { routeNames: [routeNames.persons] },
  { routeNames: [routeNames.deadlines] },
  { routeNames: [routeNames.tasks] },
  { routeNames: [routeNames.massiveEmails] },
  { routeNames: [routeNames.reports] },
  { routeNames: [routeNames.toolRuns] },
  { routeNames: [routeNames.massiveImports] },
  { routeNames: [routeNames.emailCampaigns] },
  {
    name: 'pct',
    routeNames: [routeNames.pctEvents, routeNames.pctDeposits],
  },
  {
    name: 'apb',
    routeNames: [routeNames.apbPractices, routeNames.apbProcessedEmails],
  },
  {
    name: 'userManagement',
    routeNames: [routeNames.users, routeNames.roles],
  },
  {
    name: 'systemManagement',
    routeNames: [
      routeNames.courts,
      routeNames.portfolios,
      routeNames.practiceTypes,
      routeNames.subjectTypes,
      routeNames.taskTypes,
      routeNames.recordTypes,
      routeNames.deadlineTypes,
      routeNames.massiveEmailTypes,
      routeNames.settings,
    ],
  },
]

function menuItemFromRoute(route: RouteLocation): MenuItem {
  return {
    label: t('sidebar.' + route.meta.labelKey),
    icon: route.meta.icon,
    iconText: route.meta.iconText,
    routeName: route.name,
  }
}

const items = computed<MenuItem[]>(() => {
  const itemsWithRoutes = sidebarItems.map((item) => {
    const routes = item.routeNames.map((routeName) => router.resolve({ name: routeName }))
    const visibleRoutes = routes
      .filter((route) => hasPermission(route.meta.permission))
      .filter((route) => !route.meta.featureFlag || isFeatureActive(route.meta.featureFlag))
    return {
      name: item.name,
      routes: visibleRoutes,
    }
  })
  const visibleItems = itemsWithRoutes.filter((item) => item.routes.length > 0)
  return visibleItems.map((item) => {
    if (item.routes.length === 1) {
      return menuItemFromRoute(item.routes[0])
    } else {
      return {
        key: item.name,
        label: t('sidebar.' + item.name),
        items: item.routes.map(menuItemFromRoute),
      }
    }
  })
})

const expandedKeys = ref<Record<string, boolean>>({})

const isItemActive = (item: MenuItem) =>
  item.routeName === currentRoute.name || item.routeName === currentRoute.meta.rootRouteName

const bottomItems: MenuItem[] = [
  {
    label: t('logout.button'),
    iconText: 'logout',
    icon: 'material-symbols-outlined',
    command: handleLogout,
  },
  {
    label: t('sidebar.support'),
    iconText: 'support',
    icon: 'material-symbols-outlined',
    href: import.meta.env.VITE_SUPPORT_URL,
  },
]

onMounted(() => {
  userStore.init()

  const currentRouteName = currentRoute.meta.rootRouteName ?? (currentRoute.name as RouteName)
  const item = sidebarItems.find((item) => item.routeNames.includes(currentRouteName))
  if (item?.name) {
    expandedKeys.value[item.name] = true
  }
})
</script>

<template>
  <SidebarLayout>
    <div class="m-0 flex h-full list-none flex-col p-0">
      <router-link to="/" class="mb-4 flex flex-col items-center">
        <img src="/logo.svg" alt="logo almaiura" class="h-16" />
      </router-link>

      <PanelMenu
        v-model:expanded-keys="expandedKeys"
        :model="items"
        class="flex-1 overflow-y-auto border-none"
        @click="onSidebarLinkClicked"
      >
        <template #item="{ item, active, hasSubmenu }">
          <component :is="hasSubmenu ? 'div' : 'RouterLink'" :to="{ name: item.routeName }">
            <Button
              text
              class="w-full cursor-pointer font-normal text-surface-700"
              severity="secondary"
              :pt="{
                root: [
                  'justify-start rounded',
                  {
                    'bg-surface-100': !hasSubmenu && isItemActive(item),
                  },
                ],
              }"
              :pt-options="{ mergeProps: primeVueTwMerger }"
            >
              <span
                v-if="hasSubmenu"
                class="pi pi-chevron-right mr-2 text-base transition-transform"
                :class="{ 'rotate-90': active }"
              />
              <span
                :class="[
                  item.icon,
                  {
                    'text-base font-medium text-primary': !hasSubmenu && isItemActive(item),
                  },
                ]"
                class="text-base"
              >
                {{ item?.iconText === 'undefined' ? '' : item?.iconText }}
              </span>
              <span
                class="ml-2"
                :class="{
                  'font-medium text-primary': !hasSubmenu && isItemActive(item),
                }"
              >
                {{ item.label }}
              </span>
            </Button>
          </component>
        </template>
      </PanelMenu>

      <Divider class="my-2" />

      <router-link :to="routePaths.profile" class="flex items-center justify-between p-1">
        <AvatarInfo v-if="user" :user avatar-size="normal" :text-size="'small'" />
        <i class="pi pi-chevron-right"></i>
      </router-link>

      <Menu
        :model="bottomItems"
        class="cursor-pointer border-none"
        :pt="{ menu: 'flex items-center justify-between', menuitem: 'w-full m-0' }"
        :pt-options="{ mergeProps: primeVueTwMerger }"
      >
        <template #item="{ item, props }">
          <component :is="item.href ? 'a' : 'div'" :href="item.href" target="_blank" class="block">
            <Button
              text
              :target="item.target"
              v-bind="props.action"
              class="w-full cursor-pointer"
              severity="secondary"
              :pt="{ root: 'justify-start rounded' }"
            >
              <span :class="item.icon" class="text-base">
                {{ item?.iconText === undefined ? '' : item?.iconText }}
              </span>
              <span class="ml-2">{{ item.label }}</span>
            </Button>
          </component>
        </template>
      </Menu>

      <div
        v-tooltip.top="'app: v' + appVersion + ' - server: v' + serverVersion?.version"
        class="flex justify-around"
      >
        <small>v{{ appVersion }}</small>
      </div>
    </div>
  </SidebarLayout>
</template>
